/* ----------------- React --------------- */
import { useEffect, useState } from "react";

/* ----------------- Hooks --------------- */
import useSessionStore from "../hooks/useSessionStore";

/* ----------------- Third Party Libraries --------------- */
import { useLocalSearchParams } from "expo-router";

import { events } from "@constants/tracking";
import useAnalytics from "@hooks/useAnalytics";
import { isWeb } from "@memorang/helpers/platform";
import { asPassage } from "@memorang/types";
import PassageStemModal from "../components/PassageStemModal";
import composeSessionMd from "../helpers/compose-session-md";
import { resolveItemTypeContainer } from "../helpers/item-type-registry";
import { getSlidesData } from "../helpers/session";
import { getIsSlidesHasPassage } from "../helpers/session";
import useCurrentSession from "../hooks/useCurrentSession";
import { useStimulusStore } from "../hooks/useStimulusStore";

type Props = {
	showReadMore?: boolean;
	showStartDialog?: boolean;
	propToggleShowReadMore?: () => void;
	passageStemModalRef?: React.RefObject<{
		scrollToHighlightedText: () => void;
	} | null>;
};
export const PassageStemModalContainer = ({
	showReadMore,
	showStartDialog,
	propToggleShowReadMore,
	passageStemModalRef,
}: Props) => {
	const showStimulusModal = useStimulusStore(
		(state) => state.showStimulusModal,
	);
	const setShowStimulusModal = useStimulusStore(
		(state) => state.setShowStimulusModal,
	);
	const { explicitCallTrackCustomEvent } = useAnalytics();

	const { prev } = useLocalSearchParams<{
		prev?: string;
	}>();

	const [isStart, setIsStart] = useState<boolean>(false);

	const { currentItem } = useCurrentSession();
	const updateItemStates = useSessionStore((state) => state.updateItemStates);
	const answers = useSessionStore((state) => state.answers);
	const currentAnswer = answers?.get(currentItem.id!);
	const itemState = useSessionStore((state) =>
		state.itemStates?.get(currentItem.id!),
	);

	const {
		metadata,
		stimulus: parentStimulus,
		currentStimulusItemIndex: parentIndex,
		id: itemId,
	} = currentItem;

	const parentPassage = asPassage(parentStimulus?.passage);
	const parentTitle = parentPassage?.title || parentStimulus?.className;
	const type = metadata?.type!;
	const skill = metadata?.skill;
	const parentSlides = parentStimulus?.slides;
	const parentProfessor = parentStimulus?.professor;
	const parentImages = parentStimulus?.media?.filter(
		(media) => media.type === "image" || media.type === "IMAGE",
	);
	const slideshowData = getSlidesData(parentStimulus?.slides);
	const isSlidesHasPassage = getIsSlidesHasPassage(parentSlides);
	const parentStem =
		parentStimulus?.stem?.text ||
		composeSessionMd({
			content: parentStimulus?.passage,
			highlights: currentItem.content?.highlights,
			insertText: currentItem.content?.insertSentence,
			paragraphNumber: currentItem.content?.paragraphNumber,
			choices:
				currentItem.response.type === "choice"
					? currentItem.response.choices
					: [],
			shouldFormat: !isStart,
			selectedChoiceIds: itemState?.selectedChoiceIds,
		});

	const parentInstruction = parentStimulus?.instruction;
	const parentAudio = parentStimulus?.media?.find(
		(media) => media.type === "audio" || media.type === "AUDIO",
	)?.url;

	useEffect(() => {
		setShowStimulusModal(showReadMore);
	}, [showReadMore, setShowStimulusModal]);

	useEffect(() => {
		if (itemId && !isWeb && parentStem) {
			explicitCallTrackCustomEvent({
				eventName: showStimulusModal
					? events.sessionStimulusOpened
					: events.sessionStimulusClosed,
				itemId: itemId,
				stimulusType: type,
			});
		}
	}, [
		itemId,
		type,
		showStimulusModal,
		explicitCallTrackCustomEvent,
		parentStem,
	]);

	const hasDedicatedItemTypeContainer = Boolean(
		resolveItemTypeContainer(currentItem),
	);

	useEffect(() => {
		if (
			parentIndex === 1 &&
			itemId &&
			prev !== "true" &&
			!isWeb &&
			!showStartDialog &&
			!hasDedicatedItemTypeContainer
		) {
			setShowStimulusModal(true);
			setIsStart(true);
		}
	}, [
		parentIndex,
		itemId,
		prev,
		setShowStimulusModal,
		showStartDialog,
		hasDedicatedItemTypeContainer,
	]);

	const toggleShowReadMore = () => {
		setIsStart(false);
		setShowStimulusModal(!showStimulusModal);
		propToggleShowReadMore?.();
	};

	const handleClickInsertableText = (choiceId: string) => {
		updateItemStates({
			itemId: currentItem.id!,
			selectedChoiceId: choiceId,
			multiSelect: false,
		});
	};

	const disableInsertableText = currentAnswer != null;

	return (
		<PassageStemModal
			showModal={showStimulusModal}
			toggleShowModal={toggleShowReadMore}
			mode={isStart ? "modal" : "sheet"}
			parentStem={parentStem}
			parentTitle={parentTitle}
			insertText={currentItem.content?.insertSentence}
			slideshowData={slideshowData}
			parentIndex={parentIndex}
			disableInsertableText={disableInsertableText}
			parentInstruction={
				typeof parentInstruction === "string"
					? parentInstruction
					: parentInstruction?.text
			}
			parentAudio={parentAudio}
			skill={skill}
			parentImages={parentImages}
			slides={parentSlides}
			isSlidesHasPassage={isSlidesHasPassage}
			handleClickInsertableText={handleClickInsertableText}
			professor={parentProfessor}
			ref={passageStemModalRef}
			itemId={itemId}
		/>
	);
};
